// Plant Lab: a fractal tree that grows by one repeated rule, fed by light, water and soil.
// Growth = min(light, water, soil) minus a penalty for drowning or scorching. Lesson, then do it.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { SOILS, growth, waterOptimum, treeFor, type SoilId } from './model';

interface Step {
  lesson: string;
  task: string;
  kind: 'tall' | 'bushy' | 'mark' | 'choice' | 'dark' | 'soil' | 'scorch';
  choices?: string[];
  answer?: number;
  preset?: { light?: number; water?: number; soil?: SoilId; splits?: number };
  why: string;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { lesson: 'A tree grows by repeating one rule: every branch splits into smaller branches. Give it light, water and good soil and it keeps repeating.', task: 'Set light, water and soil so the tree grows tall, then tap Grow.', kind: 'tall', preset: { light: 30, water: 30, soil: 'sand' }, why: 'Plenty of light, plenty of water and rich soil: the tree repeats its rule many times and gets tall.' },
    { lesson: 'How bushy a tree looks depends on how many branches each branch splits into.', task: 'Make a bushy tree: at least 3 splits, and still healthy. Tap Grow.', kind: 'bushy', preset: { splits: 2 }, why: 'More splits per branch means many more twigs and leaves at the top.' },
    { lesson: 'Plants cannot walk to find food. They have to make it where they stand.', task: 'What does a plant need to grow well?', kind: 'choice', choices: ['Light, water and good soil', 'Only water', 'Only soil', 'Darkness and cold'], answer: 0, why: 'Light for energy, water to move things around, soil for minerals and a place for roots.' },
  ],
  2: [
    { lesson: 'More water helps a plant, but only up to a point. After that, extra water does nothing, and too much drowns the roots.', task: 'Light is set to 60. Slide water up slowly. When growth stops rising, tap Mark.', kind: 'mark', preset: { light: 60, water: 10, soil: 'loam' }, why: 'Growth rose until water matched the light, then went flat. The plant can only use as much water as its light allows.' },
    { lesson: 'Leaves are little food factories. This is called photosynthesis.', task: 'What do leaves use to make the plant\'s food?', kind: 'choice', choices: ['Sunlight, water and air', 'Soil only', 'Rain only', 'Wind'], answer: 0, why: 'Leaves take sunlight, water from the roots and carbon dioxide from the air, and make sugar. Oxygen comes out.' },
    { lesson: 'Too much of a good thing is a problem too. Strong light with little water dries a plant out.', task: 'Set light to 100 and water to 20, tap Grow, and watch. What happened?', kind: 'scorch', choices: ['The leaves scorched and growth dropped', 'It grew huge', 'Nothing changed', 'It turned blue'], answer: 0, preset: { soil: 'loam' }, why: 'Lots of light with little water: the leaves lose water faster than the roots can bring it. They scorch.' },
  ],
  3: [
    { lesson: 'Scientists predict first, then test. A plant is put in a dark closet with plenty of water and good soil.', task: 'Predict: what will happen to the plant in the dark?', kind: 'choice', choices: ['It will barely grow at all', 'It will grow faster', 'It will grow the same', 'It will grow but turn blue'], answer: 0, why: 'No light means no food, no matter how much water and soil. Now test it: set light to 0 and tap Grow.' },
    { lesson: 'Now test your prediction.', task: 'Set light to 0 and tap Grow. Then tap Check.', kind: 'dark', preset: { water: 80, soil: 'loam' }, why: 'Confirmed. Without light the tree stays a stub. Light is the energy for everything else.' },
    { lesson: 'Leaves on a plant turn toward a window over a few days.', task: 'Why do leaves turn to face the light?', kind: 'choice', choices: ['To catch more light to make food', 'To stay warm at night', 'To look pretty', 'To hide from rain'], answer: 0, why: 'More light caught means more food made. Plants grow toward light; it is called phototropism.' },
  ],
};

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let explore = false;
  let attempts = 0, correct = 0, missed = false;
  const t0 = performance.now();
  let tStep = performance.now();

  // inputs
  let light = 60, water = 60, soil: SoilId = 'loam', splits = 2, ang = 26, seed = 3;
  // tree animation
  let grow = 0, growing = false, tm = 0;
  let g = growth(light / 100, water / 100, soil);
  let tree = treeFor(g, splits);
  let lastGrownG = 0;       // growth value when Grow last finished
  let grewSinceStep = false;
  let leafCount = 0;

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const stage = h('div');
  const stats = h('div.grid2', null, statBox('Health', '0%'), statBox('Leaves', '0'));
  const lightS = slider('Light', 0, 100, light, v => { light = v; recompute(); });
  const waterS = slider('Water', 0, 100, water, v => { water = v; recompute(); });
  const splitS = slider('Splits', 2, 4, splits, v => { splits = v; recompute(); });
  const soilRow = h('div.grid3');
  const growBtn = h('button.btn.primary', { onclick: () => { grow = 0; growing = true; ctx.sound('pop'); } }, 'Grow');
  const actionBtn = h('button.btn', { onclick: act }, 'Check');
  const choices = h('div.grid2');
  host.append(lesson, task, stage, stats, lightS.row, waterS.row, splitS.row, soilRow, h('div.grid2', null, growBtn, actionBtn), choices);
  const S = makeSurface(stage, { aspect: 0.8 });

  function statBox(label: string, v: string) { return h('div.card', { style: { padding: '8px 12px' } }, h('div.muted.small', null, label), h('div', { style: { fontSize: '22px', fontWeight: '800' } }, v)); }
  function slider(label: string, min: number, max: number, val: number, on: (v: number) => void) {
    const input = h('input', { type: 'range', min: String(min), max: String(max), step: '1', value: String(val), 'aria-label': label }) as HTMLInputElement;
    const out = h('span', { style: { minWidth: '36px', textAlign: 'right', fontWeight: '800' } }, String(val));
    input.addEventListener('input', () => { out.textContent = input.value; on(Number(input.value)); });
    return { row: h('div.row', null, h('span.muted.small', { style: { minWidth: '52px' } }, label), input, out), input, out };
  }
  function renderSoil() {
    clear(soilRow);
    for (const s of SOILS) soilRow.append(h('button.btn.small' + (soil === s.id ? '.primary' : ''), { onclick: () => { soil = s.id; ctx.say(s.note); recompute(); renderSoil(); } }, s.label));
  }
  function recompute() {
    g = growth(light / 100, water / 100, soil);
    tree = treeFor(g, splits);
    if (!growing) grow = Math.min(grow, tree.levels);
  }
  function setInputs(p: Step['preset']) {
    if (!p) return;
    if (p.light !== undefined) { light = p.light; lightS.input.value = String(light); lightS.out.textContent = String(light); }
    if (p.water !== undefined) { water = p.water; waterS.input.value = String(water); waterS.out.textContent = String(water); }
    if (p.splits !== undefined) { splits = p.splits; splitS.input.value = String(splits); splitS.out.textContent = String(splits); }
    if (p.soil) soil = p.soil;
    recompute(); renderSoil();
  }

  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of 3: ${st.task}`;
    clear(choices);
    missed = false; grewSinceStep = false; tStep = performance.now();
    setInputs(st.preset);
    grow = 0; growing = true;
    if (st.kind === 'choice') { actionBtn.style.display = 'none'; st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '16px' }, onclick: () => answer(i) }, c))); }
    else if (st.kind === 'scorch') { actionBtn.style.display = 'none'; }
    else { actionBtn.style.display = ''; actionBtn.textContent = st.kind === 'mark' ? 'Mark' : 'Check'; }
    ctx.speak(st.task);
  }

  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: 'biology', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `plant:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    if (tier === 2 && st.kind === 'choice' && si === 1) ctx.charm('charm.photosyn');
    si++;
    if (si >= steps.length) {
      if (tier === 1) ctx.charm('charm.tree');
      explore = true;
      task.textContent = 'Round complete. Keep experimenting with the sliders.';
      actionBtn.style.display = 'none'; clear(choices);
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1600);
      return;
    }
    setTimeout(showStep, 1800);
  }
  function fail(st: Step, hint: string) {
    if (!missed) { attempts++; missed = true; ctx.report({ skill: 'biology', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `plant:${tier}:${si}` }); }
    ctx.sound('buzz'); ctx.say(hint);
    void st;
  }

  function act() {
    const st = steps[si]; if (!st || explore) return;
    if (st.kind === 'tall') {
      if (!grewSinceStep || growing) { ctx.say('Tap Grow first and wait for the tree to finish.'); return; }
      if (lastGrownG >= 0.7) pass(st);
      else fail(st, g < 0.3 ? 'Still small. Raise light AND water, and pick the richest soil.' : light < 70 ? 'More light.' : water < 70 ? 'More water.' : soil !== 'loam' ? 'Try the loam soil.' : 'Almost. Balance light and water.');
    } else if (st.kind === 'bushy') {
      if (!grewSinceStep || growing) { ctx.say('Tap Grow first and wait for the tree to finish.'); return; }
      if (splits >= 3 && lastGrownG >= 0.5) pass(st);
      else fail(st, splits < 3 ? 'Slide Splits up to 3 or 4, then Grow.' : 'Bushy, but not healthy. Give it more light and water too.');
    } else if (st.kind === 'mark') {
      const opt = waterOptimum(light / 100, soil) * 100;
      if (Math.abs(water - opt) <= 10) pass(st);
      else fail(st, water < opt - 10 ? `Growth is still rising at water ${water}. Keep sliding up, slowly.` : `You went past it. Growth stopped rising earlier. Slide back down.`);
    } else if (st.kind === 'dark') {
      if (!grewSinceStep || growing) { ctx.say('Set light to 0, tap Grow, and wait.'); return; }
      if (light <= 5 && lastGrownG <= 0.05) pass(st);
      else fail(st, light > 5 ? 'Slide light all the way down to 0 first.' : 'Tap Grow with the light at 0, then Check.');
    }
  }

  function answer(i: number) {
    const st = steps[si];
    if (i === st.answer) pass(st);
    else fail(st, st.kind === 'scorch' ? 'Look at the Health number after it grew with light 100 and water 20.' : 'Think about what the sliders did to the tree.');
  }

  function update(dt: number) {
    tm += dt;
    if (growing) {
      grow += dt * tree.levels / 3.2;
      if (grow >= tree.levels) {
        grow = tree.levels; growing = false; lastGrownG = g; grewSinceStep = true;
        const st = explore ? null : steps[si];
        if (st && st.kind === 'scorch' && light >= 90 && water <= 30) { clear(choices); st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '16px' }, onclick: () => answer(i) }, c))); ctx.say(`Health ${Math.round(g * 100)}%. What happened?`); }
      }
    }
  }

  // ---- the fractal tree (rule from the widget) ----
  function rnd(n: number): number { const x = Math.sin(n * 12.9898 + seed * 78.233) * 43758.5453; return x - Math.floor(x); }
  let leaves: [number, number, number][] = [];
  function branch(c: CanvasRenderingContext2D, x: number, y: number, len: number, a: number, l: number, id: number, W: number) {
    const lv = tree.levels;
    if (l >= lv) return;
    const f = Math.max(0, Math.min(1, grow - l));
    if (f <= 0) return;
    const aa = a + Math.sin(tm * 1.6 + l * 0.6 + rnd(id) * 2) * 0.006 * (l + 1);
    const L = len * f, x2 = x + Math.sin(aa) * L, y2 = y - Math.cos(aa) * L;
    c.strokeStyle = l < 3 ? '#633806' : l < 6 ? '#854F0B' : '#BA7517';
    c.lineWidth = Math.max(0.6, (W / 380) * 11 * Math.pow(0.68, l));
    c.beginPath(); c.moveTo(x, y); c.lineTo(x2, y2); c.stroke();
    if (l === lv - 1) { if (f >= 1) leaves.push([x2, y2, id]); return; }
    if (f < 1) return;
    for (let k = 0; k < splits; k++) {
      const off = (k - (splits - 1) / 2) * ang * Math.PI / 180;
      const j = (rnd(id * 5 + k + 1) - 0.5) * 0.28, jl = 1 + (rnd(id * 5 + k + 101) - 0.5) * 0.24;
      branch(c, x2, y2, len * 0.72 * jl, aa + off + j, l + 1, id * 5 + k + 1, W);
    }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h, GROUND = H * 0.88;
    const sky = light < 15 ? '#2a2a3a' : light < 50 ? '#cfdce8' : '#B5D4F4';
    c.fillStyle = sky; c.fillRect(0, 0, W, H);
    // sun by light
    if (light >= 15) { c.fillStyle = '#FAC775'; c.globalAlpha = 0.3 + light / 140; c.beginPath(); c.arc(W * 0.85, H * 0.14, 10 + light * 0.25, 0, 7); c.fill(); c.globalAlpha = 1; }
    // rain drops by water
    c.strokeStyle = 'rgba(79,143,230,.5)'; c.lineWidth = 1.5;
    const drops = Math.round(water / 6);
    for (let i = 0; i < drops; i++) { const dx = rnd(i * 13) * W, dy = (rnd(i * 29) * GROUND + tm * 90 * (0.6 + rnd(i))) % GROUND; c.beginPath(); c.moveTo(dx, dy); c.lineTo(dx - 2, dy + 8); c.stroke(); }
    // ground by soil
    c.fillStyle = soil === 'sand' ? '#e4cfa0' : soil === 'clay' ? '#b5744a' : '#5c3a21'; c.fillRect(0, GROUND, W, H - GROUND);
    // tree
    let sum = 0; for (let i = 0; i < tree.levels; i++) sum += Math.pow(0.72, i);
    const base = Math.min((GROUND - 14) / (sum * 0.92), GROUND * 0.34) * (0.6 + 0.4 * g);
    leaves = []; c.lineCap = 'round';
    branch(c, W / 2, GROUND, base, 0, 0, 0, W);
    const healthy = g;
    const leafCols = healthy > 0.66 ? ['#3B6D11', '#639922', '#97C459'] : healthy > 0.33 ? ['#97C459', '#BA7517', '#EF9F27'] : ['#BA7517', '#D85A30', '#8a5a3b'];
    const r = Math.max(1.6, Math.min(5, base * Math.pow(0.72, tree.levels) * 0.35)) * tree.leafScale;
    for (const q of leaves) { c.fillStyle = leafCols[Math.floor(rnd(q[2] + 7) * 3)]; c.beginPath(); c.arc(q[0], q[1], r, 0, 7); c.fill(); }
    leafCount = leaves.length;
    (stats.children[0].lastElementChild as HTMLElement).textContent = `${Math.round(g * 100)}%`;
    (stats.children[1].lastElementChild as HTMLElement).textContent = String(leafCount);
  }

  renderSoil();
  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.plant-lab',
  title: 'Plant Lab',
  blurb: 'Grow a tree by one repeated rule. Feed it light, water and soil, and find what it really needs.',
  category: 'science',
  skills: ['biology'],
  levelRange: [1, 10],
  unlock: { stars: 9 },
  mount,
};
export default mod;
